/**
 * CalendarWidget — Next 7 days at a glance on the dashboard.
 *
 * Reads /api/calendar/upcoming?days=7&limit=5.
 * Hides itself entirely if there are zero events (avoids dashboard clutter
 * for brand-new accounts).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarIcon as CalendarIcon, CakeIcon as Cake, DropIcon as Drop, HeartStraightIcon as HeartStraight, BabyIcon as Baby, TruckIcon as Truck, MapPinLineIcon as MapPinLine, VideoCameraIcon as VideoCamera, AirplaneTiltIcon as AirplaneTilt, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

const ICON_MAP = {
  Cake, Drop, DropHalf: Drop, HeartStraight, Baby, Truck, MapPinLine,
  VideoCamera, AirplaneTilt, Calendar: CalendarIcon,
};

export default function CalendarWidget() {
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/calendar/upcoming", { params: { days: 7, limit: 5 } })
      .then((r) => {
        if (!mounted) return;
        setEvents(r.data.events || []);
        setMeta(r.data.source_meta || {});
      })
      .catch(() => {})
      .finally(() => mounted && setLoaded(true));
    return () => { mounted = false; };
  }, []);

  if (!loaded) return null;
  const isEmpty = events.length === 0;

  return (
    <section data-testid="dashboard-calendar-widget" className="border border-[#EAEAEA] rounded-2xl bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#EAEAEA]">
        <h3 className="font-serif text-base text-[#0E0E0E] inline-flex items-center gap-2">
          <CalendarIcon size={16} className="text-[#D4AF37]" />
          Next 7 days
        </h3>
        <Link
          to="/calendar"
          className="text-xs text-[#6B6B6B] hover:text-[#0E0E0E] inline-flex items-center gap-1"
          data-testid="dashboard-calendar-view-all"
        >
          Full calendar <ArrowRight size={12} />
        </Link>
      </div>
      {isEmpty ? (
        <div className="px-4 py-6 text-center" data-testid="dashboard-calendar-empty">
          <p className="text-sm text-[#0E0E0E] font-medium">No events in the next 7 days.</p>
          <p className="text-[11px] text-[#6B6B6B] mt-1 max-w-md mx-auto">
            Heat cycles, breedings, and transport pickups appear here automatically.
            Add a vet appointment, training class, or any custom reminder.
          </p>
          <Link
            to="/calendar"
            className="inline-block mt-3 px-4 py-1.5 text-xs rounded-full bg-[#0E0E0E] text-white hover:bg-[#1F1F1F]"
            data-testid="dashboard-calendar-empty-cta"
          >
            Open calendar
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-[#EAEAEA]">
        {events.map((ev) => {
          const m = meta[ev.source] || { color: "#525252", icon: "Calendar", label: ev.source };
          const Icon = ICON_MAP[m.icon] || CalendarIcon;
          const d = new Date(ev.date);
          const dayLabel = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
          const Content = (
            <div className="flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFA] transition-colors">
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
                style={{ background: `${m.color}1A`, color: m.color }}
              >
                <Icon size={15} />
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm text-[#0E0E0E] truncate ${ev.tentative ? "italic" : ""}`}>{ev.title}</p>
                <p className="text-[11px] text-[#6B6B6B] mt-0.5">
                  {dayLabel} · <span style={{ color: m.color }}>{m.label}</span>
                  {ev.tentative && " · tentative"}
                </p>
              </div>
            </div>
          );
          return ev.url ? (
            <li key={ev.id}><Link to={ev.url} data-testid={`dashboard-event-${ev.id}`}>{Content}</Link></li>
          ) : (
            <li key={ev.id} data-testid={`dashboard-event-${ev.id}`}>{Content}</li>
          );
        })}
        </ul>
      )}
    </section>
  );
}
